'use client';

import { Icon } from '@/components/ui';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { BRAND } from '@/lib/cms/config';
import { EVENT_NAME } from '@/lib/gtm/const';
import { Link } from '@/lib/i18n/navigation';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import styles from './RegisterWatch.module.scss';

function RichTextBold(chunks: ReactNode) {
  return <strong>{chunks}</strong>;
}

export interface RegisterWatchProps {
  className?: string;
}

/**
 * Register watch CTA section promoting blockchain-based ownership
 * Gallet: Two-column layout with image, hardcoded text
 * Breitling: Centered layout with icon, split-color heading, i18n text
 */
export function RegisterWatch({ className }: RegisterWatchProps) {
  const t = useTranslations('product.registerWatch');
  const { sendIntentionEvent } = useGoogleAnalytics();

  const handleRegisterWatchClick = () => {
    sendIntentionEvent(EVENT_NAME.INTENTION_REGISTER_WATCH);
  };

  // Gallet: Two-column layout with image
  if (BRAND === 'gallet') {
    return (
      <section className={clsx(styles.section, className)}>
        {/* Image Panel */}
        <div className={styles.imagePanel}>
          <img
            src="/images/figma-exports/1e860dc12067fd128a35d5fdeb17fc8917f1c2c4.png"
            alt={t('gallet.imageAlt')}
            className={styles.image}
          />
        </div>

        {/* Content Panel */}
        <div className={styles.contentPanel}>
          <div className={styles.content}>
            <div className={styles.header}>
              <h2 className={styles.title}>{t('gallet.title')}</h2>
              <p className={styles.subtitle}>{t('gallet.subtitle')}</p>
            </div>

            <div className={styles.body}>
              <p>{t.rich('gallet.bodyIntro', { b: RichTextBold })}</p>
              <p>{t.rich('gallet.bodyDetail', { b: RichTextBold })}</p>
            </div>

            <Link href="/register" className={styles.cta} onClick={handleRegisterWatchClick}>
              <span>{t('gallet.cta')}</span>
              <Icon name="arrow-right" size={16} />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Breitling: Centered layout with icon
  return (
    <section className={clsx(styles.section, className)}>
      <div className={styles.container}>
        {/* Watch Icon */}
        <div className={styles.iconWrapper}>
          <img
            src="/icons/register-watch-icon.svg"
            alt={t('iconAlt')}
            className={styles.icon}
            width={48}
            height={48}
            onError={(e) => {
              // Fallback if icon doesn't exist
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        {/* Header - split color title */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            <span className={styles.titleAccent}>{t('titleAccent')}</span> {t('titleRest')}
          </h2>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>

        {/* Body text */}
        <div className={styles.body}>
          <p>{t('description')}</p>
        </div>

        {/* CTA */}
        <Link
          href="/my-account/my-watches"
          className={styles.cta}
          onClick={handleRegisterWatchClick}
        >
          <span>{t('cta')}</span>
          <Icon name="arrow-right" size={16} />
        </Link>
      </div>
    </section>
  );
}

RegisterWatch.displayName = 'RegisterWatch';
